import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useChatConnection, type ServerMessage, type DebugSummary, type SuggestionItem } from "@/hooks/useChatConnection";
import { useIOSKeyboard } from "@/hooks/useIOSKeyboard";
import { useAutoScroll } from "@/hooks/useAutoScroll";
import { api, type Project } from "@/lib/api";
import { buildHeroGreeting } from "@/lib/greeting";
import { relativeTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { getDeviceKind } from "@/lib/device";
import { Markdown } from "@/components/chat/Markdown";
import { Linkify } from "@/components/chat/Linkify";
import { ActivityGroup } from "@/components/chat/ActivityGroup";
import { WorkProgressCard, type WorkStep } from "@/components/chat/WorkProgressCard";
import type { IterationData } from "@/components/chat/IterationControls";
import { CopyMessageButton } from "@/components/chat/CopyMessageButton";
import { ScrollToBottom } from "@/components/chat/ScrollToBottom";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { DateSeparator } from "@/components/chat/DateSeparator";
import { HeaderMenu } from "@/components/chat/HeaderMenu";
import { ProjectSettingsPanel } from "@/components/chat/ProjectSettingsPanel";
import { BranchSwitcher } from "@/components/chat/BranchSwitcher";
import { WidgetRenderer } from "@/components/widgets";
import { ArrowUp, Square, Loader2, Menu } from "lucide-react";
import { DaydraLogo } from "@/components/DaydraLogo";

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "error" | "activity" | "widget";
  content: string;
  streaming?: boolean;
  timestamp: Date;
  widgetType?: string;
  widgetData?: Record<string, unknown>;
  widgetActions?: Array<{ id: string; label: string; style?: string }>;
  /** Set on the last message of a work sequence when budget was hit. */
  budgetHit?: boolean;
  /** Whether the user can manually continue from this budget hit. */
  canContinue?: boolean;
  /** Branch name if code was pushed during this work sequence. */
  pushedBranch?: string;
  /** Parsed diff files for the progress card diff viewer. */
  diffFiles?: unknown[];
  /** Classifier task type (code_change, code_read, git_op, conversation). */
  taskType?: string;
  /** Iteration data for single-parameter tweaking. */
  iterationData?: unknown;
}

// Deterministic gradient per message ID — subtle direction/hue shifts
const USER_GRADIENTS = [
  "linear-gradient(135deg, rgba(92,147,240,0.08) 0%, rgba(70,120,220,0.03) 100%)",
  "linear-gradient(150deg, rgba(80,135,235,0.07) 0%, rgba(100,160,245,0.04) 100%)",
  "linear-gradient(120deg, rgba(100,155,245,0.09) 0%, rgba(75,125,225,0.03) 100%)",
  "linear-gradient(140deg, rgba(85,140,238,0.07) 0%, rgba(110,165,250,0.04) 100%)",
  "linear-gradient(160deg, rgba(95,150,242,0.08) 0%, rgba(65,115,215,0.03) 100%)",
];
const ASSISTANT_GRADIENTS = [
  "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)",
  "linear-gradient(150deg, rgba(130,95,245,0.08) 0%, rgba(115,82,228,0.04) 100%)",
  "linear-gradient(120deg, rgba(120,88,235,0.09) 0%, rgba(128,95,240,0.04) 100%)",
  "linear-gradient(140deg, rgba(128,94,242,0.08) 0%, rgba(105,75,218,0.04) 100%)",
  "linear-gradient(160deg, rgba(135,98,248,0.08) 0%, rgba(118,85,232,0.04) 100%)",
];
function bubbleGradient(id: string, gradients: string[]): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return gradients[Math.abs(hash) % gradients.length]!;
}

type RenderItem =
  | { kind: "message"; msg: ChatMessage }
  | { kind: "activities"; ids: string[]; texts: string[] }
  | { kind: "work_sequence"; steps: WorkStep[]; id: string; budgetHit?: boolean; canContinue?: boolean; pushedBranch?: string; diffFiles?: unknown[]; taskType?: string; iterationData?: unknown }
  | { kind: "date"; date: Date; id: string };

function groupMessages(messages: ChatMessage[]): RenderItem[] {
  // First pass: build flat items with date separators + activity batching
  const flat: RenderItem[] = [];
  let activityBatch: { ids: string[]; texts: string[] } | null = null;
  let lastDate: string | null = null;

  for (const msg of messages) {
    if (msg.role === "activity") {
      if (!activityBatch) {
        activityBatch = { ids: [msg.id], texts: [msg.content] };
      } else {
        activityBatch.ids.push(msg.id);
        activityBatch.texts.push(msg.content);
      }
    } else {
      const dayKey = msg.timestamp.toDateString();
      if (dayKey !== lastDate) {
        if (activityBatch) {
          flat.push({ kind: "activities", ...activityBatch });
          activityBatch = null;
        }
        flat.push({ kind: "date", date: msg.timestamp, id: `date-${dayKey}` });
        lastDate = dayKey;
      }

      if (activityBatch) {
        flat.push({ kind: "activities", ...activityBatch });
        activityBatch = null;
      }
      flat.push({ kind: "message", msg });
    }
  }

  if (activityBatch) {
    flat.push({ kind: "activities", ...activityBatch });
  }

  // Second pass: collapse consecutive (assistant → activities → assistant → ...)
  // runs into work_sequence items when there are 2+ assistant/activity pairs.
  const items: RenderItem[] = [];
  let i = 0;

  while (i < flat.length) {
    const cur = flat[i]!;

    // Try to start a work sequence: needs assistant, then activities, then assistant...
    if (
      cur.kind === "message" &&
      cur.msg.role === "assistant" &&
      i + 1 < flat.length &&
      flat[i + 1]!.kind === "activities"
    ) {
      // Peek ahead to see if there are 2+ pairs
      const seqStart = i;
      const steps: WorkStep[] = [];
      let j = i;

      while (j < flat.length) {
        const item = flat[j]!;

        if (item.kind === "message" && item.msg.role === "assistant") {
          steps.push({
            text: item.msg.content || undefined,
            activities: [],
            streaming: item.msg.streaming,
            msgId: item.msg.id,
          });
          j++;
        } else if (item.kind === "activities" && steps.length > 0) {
          steps[steps.length - 1]!.activities = item.texts;
          j++;
        } else {
          break;
        }
      }

      // A single assistant+activity pair is enough when the card is seeded
      // by work_start (sync step). Require 2+ pairs only without sync.
      if (steps.length >= 1 && steps[0]!.activities.length > 0) {
        const firstMsg = (flat[seqStart] as { kind: "message"; msg: ChatMessage }).msg;
        // Check if any message in the sequence was marked as budget-hit (from activity_log)
        const budgetHit = flat.slice(seqStart, j).some(
          (f) => f.kind === "message" && f.msg.budgetHit,
        ) || undefined;
        // Find pushed branch and diff files from any message in the sequence
        const seqMsgs = flat.slice(seqStart, j).filter((f): f is { kind: "message"; msg: ChatMessage } => f.kind === "message");
        const pushedBranch = seqMsgs.reduce<string | undefined>(
          (acc, f) => acc || f.msg.pushedBranch,
          undefined,
        );
        const diffFiles = seqMsgs.reduce<unknown[] | undefined>(
          (acc, f) => acc || f.msg.diffFiles,
          undefined,
        );
        const canContinue = seqMsgs.some((f) => f.msg.canContinue) || undefined;
        const taskType = seqMsgs.reduce<string | undefined>(
          (acc, f) => acc || f.msg.taskType,
          undefined,
        );
        const iterationData = seqMsgs.reduce<unknown | undefined>(
          (acc, f) => acc || f.msg.iterationData,
          undefined,
        );
        items.push({ kind: "work_sequence", steps, id: `ws-${firstMsg.id}`, budgetHit, canContinue, pushedBranch, diffFiles, taskType, iterationData });
        i = j;
        continue;
      }
    }

    items.push(cur);
    i++;
  }

  return items;
}

// ── Debug bar ────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  return `${(n / 1000).toFixed(1)}k`;
}

function DebugBar({ summary }: { summary: DebugSummary }) {
  const [expanded, setExpanded] = useState(false);

  const duration_ms = summary.duration_ms ?? 0;
  const totals = summary.totals;
  const classifier = summary.classifier;
  const agent_loop = summary.agent_loop;
  const lane = summary.lane ?? summary.fast_path_type ?? "unknown";

  const totalTokens = totals?.total_tokens ?? 0;
  const toolCalls = totals?.tool_calls ?? agent_loop?.tool_calls ?? 0;
  const taskLabel = classifier
    ? `${classifier.task_type ?? "?"} · ${classifier.model ?? lane}`
    : lane;

  const debugLines: string[] = [
    `${formatDuration(duration_ms)} · ${formatTokens(totalTokens)} tok · ${toolCalls} tools · ${taskLabel}`,
  ];

  if (expanded) {
    if (classifier) {
      debugLines.push("");
      debugLines.push(`classifier: ${formatDuration(classifier.duration_ms ?? 0)} · ${formatTokens((classifier.input_tokens ?? 0) + (classifier.output_tokens ?? 0))} tok`);
      debugLines.push(`  type: ${classifier.task_type ?? "?"} · model: ${classifier.model ?? "?"} · tools: [${classifier.tool_groups?.join(", ") ?? ""}]`);
    }
    if (agent_loop) {
      debugLines.push("");
      debugLines.push(`agent: ${formatTokens((agent_loop.input_tokens ?? 0) + (agent_loop.output_tokens ?? 0))} tok · ${agent_loop.tool_calls ?? 0} tools · ${agent_loop.iterations ?? 0} iterations`);
      if (agent_loop.files_changed?.length) {
        for (const f of agent_loop.files_changed) {
          debugLines.push(`  ${f.action}: ${f.path}`);
        }
      }
    }
    if (totals) {
      debugLines.push("");
      debugLines.push(`${totals.input_tokens ?? 0} in + ${totals.output_tokens ?? 0} out = ${totals.total_tokens ?? 0} total · ${totals.api_calls ?? 0} API calls`);
    }
    if (summary.governor?.complexity) {
      const cx = summary.governor.complexity;
      debugLines.push("");
      debugLines.push(`complexity: ${cx.score ?? "?"}/100 → ${cx.model ?? "?"} ${cx.phased ? "(phased)" : ""}`);
      if (cx.reasoning) debugLines.push(`  ${cx.reasoning}`);
    }
  }

  return (
    <pre
      className="mt-2 text-[10px] font-mono text-daydra-muted/30 cursor-pointer hover:text-daydra-muted/50 transition-colors whitespace-pre-wrap select-text leading-relaxed"
      onClick={() => setExpanded((e) => !e)}
    >
      {debugLines.join("\n")}
    </pre>
  );
}

type HeroPhase = "hero" | "collapsing" | "done";

// Hero greeting — dynamic text size based on message length
function heroFontSize(text: string): string {
  const len = text.length;
  if (len < 40) return "text-5xl sm:text-6xl";
  if (len < 80) return "text-4xl sm:text-5xl";
  if (len < 140) return "text-3xl sm:text-4xl";
  return "text-2xl sm:text-3xl";
}

// ── ChatShell — unified chat page ──────────────────────────────

export default function ChatShell() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // Core context state
  const [activeProjectId, setActiveProjectId] = useState<string | null>(urlProjectId ?? null);
  const isConcierge = activeProjectId === null;

  // Sync with URL if browser back/forward changes the route param
  useEffect(() => {
    const fromUrl = urlProjectId ?? null;
    if (fromUrl !== activeProjectId) {
      setActiveProjectId(fromUrl);
    }
  }, [urlProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [project, setProject] = useState<Project | null>(null);
  const [currentBranch, setCurrentBranch] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const activeSessionRef = useRef<string | null>(null);
  const forwardPromptRef = useRef<string | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const lastBudgetStatusRef = useRef<"complete" | "budget">("complete");
  const lastPushedBranchRef = useRef<string | undefined>(undefined);
  const lastCanContinueRef = useRef(false);
  const lastTaskTypeRef = useRef<string | undefined>(undefined);
  const continuingRef = useRef(false);
  const [lastDiffFiles, setLastDiffFiles] = useState<unknown[] | undefined>(undefined);
  const [lastIterationData, setLastIterationData] = useState<unknown | undefined>(undefined);
  // Per-card iteration state: tracks apply/push progress and showPush flag
  const [iterationStates, setIterationStates] = useState<Record<string, { applying?: boolean; pushing?: boolean; showPush?: boolean; data?: unknown }>>({});
  const [onboarding, setOnboarding] = useState(false);
  const [widgetInputFocused, setWidgetInputFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [debugSummaries, setDebugSummaries] = useState<Record<string, DebugSummary>>({});
  const [contextUsage, setContextUsage] = useState<{ percent: number; shouldWarn: boolean } | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [, setTick] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const streamingIdRef = useRef<string | null>(null);
  const lastAssistantIdRef = useRef<string | null>(null);
  const taskResumeRef = useRef(false); // True while replaying buffered events from task_resume
  const animatedMsgIds = useRef(new Set<string>());
  const [entering, setEntering] = useState(false);
  const pendingEnterRef = useRef(true);
  const [messagesHidden, setMessagesHidden] = useState(true);

  // Hero greeting state — large centered greeting on concierge first load
  const [heroPhase, setHeroPhase] = useState<HeroPhase>("hero");
  const heroTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inputRevealing, setInputRevealing] = useState(false);

  // Track when a project switch originates from hero phase —
  // lets us skip showing concierge messages as chat bubbles during the transition
  const heroOriginRef = useRef(false);

  // Track current mode for message handler
  const modeRef = useRef<"concierge" | "project">(isConcierge ? "concierge" : "project");
  modeRef.current = isConcierge ? "concierge" : "project";

  // Skip hero mode when not on concierge
  useEffect(() => {
    if (!isConcierge) setHeroPhase("done");
  }, [isConcierge]);

  // Client-side hero greeting — no WebSocket needed
  const heroSeedRef = useRef(Date.now());
  useEffect(() => {
    if (!isConcierge) return;
    let cancelled = false;

    async function loadGreeting() {
      try {
        const [projects, githubStatus] = await Promise.all([
          api.projects.list(),
          api.github.status().catch(() => ({ connected: false, installations: [] })),
        ]);
        if (cancelled) return;
        const result = buildHeroGreeting({
          projects,
          hasGitHub: githubStatus.connected,
          seed: heroSeedRef.current,
        });
        setMessages((prev) => {
          if (prev.length > 0) return prev;
          return [{
            id: crypto.randomUUID(),
            role: "assistant" as const,
            content: result.text,
            timestamp: new Date(),
          }];
        });
        if (result.suggestions.length > 0) {
          setSuggestions(result.suggestions);
        }
      } catch {
        // Greeting is non-critical — silently fail
      }
    }

    loadGreeting();
    return () => { cancelled = true; };
  }, [isConcierge]);

  // Clean up hero timer
  useEffect(() => {
    return () => { if (heroTimerRef.current) clearTimeout(heroTimerRef.current); };
  }, []);

  const { isKeyboardOpen, isKeyboardTransitioning } = useIOSKeyboard(scrollRef, inputAreaRef, inputRef);

  const { isScrolledUp, handleScroll: onAutoScroll, scrollToBottom, resetScroll } =
    useAutoScroll(scrollRef, [messages, debugSummaries], { isKeyboardTransitioning });

  // ── Context switching ──

  const pendingSwitchRef = useRef<{ projectId: string | null; forwardPrompt?: string } | null>(null);

  const switchContext = useCallback((newProjectId: string | null, forwardPrompt?: string, skipAnimation?: boolean) => {
    if (newProjectId === activeProjectId) return;

    // Store the pending switch and trigger exit animation
    pendingSwitchRef.current = { projectId: newProjectId, forwardPrompt };
    setTransitioning(true);

    // Skip exit animations when coming straight from hero to project
    if (messages.length === 0 || skipAnimation) {
      completeSwitchNow(newProjectId, forwardPrompt);
    }
  }, [activeProjectId, messages.length]);

  function completeSwitchNow(newProjectId: string | null, forwardPrompt?: string) {
    pendingSwitchRef.current = null;
    heroOriginRef.current = false;
    setMessages([]);
    setTransitioning(false);
    setDebugSummaries({});
    setContextUsage(null);
    setSuggestions([]);
    setIsBusy(false);
    setOnboarding(false);
    setLoadingHistory(false);
    setLoadingOlder(false);
    setHasMore(true);
    setProject(null);

    streamingIdRef.current = null;
    lastAssistantIdRef.current = null;
    animatedMsgIds.current.clear();
    setEntering(false);
    setMessagesHidden(true);
    pendingEnterRef.current = true;
    activeSessionRef.current = null;
    forwardPromptRef.current = forwardPrompt ?? null;

    // Reset hero for fresh concierge, skip for projects
    setHeroPhase(newProjectId === null ? "hero" : "done");

    setActiveProjectId(newProjectId);

    const newUrl = newProjectId ? `/chat/${newProjectId}` : "/";
    window.history.replaceState({}, "", newUrl);
  }

  // Called when any exit animation finishes
  function handleAnimationEnd(e: React.AnimationEvent) {
    if (!pendingSwitchRef.current) return;
    const animName = e.animationName;
    if (animName !== "msgExitRight" && animName !== "msgExitLeft") return;

    // Hide messages, then let the browser paint before clearing
    setMessagesHidden(true);
    const { projectId, forwardPrompt } = pendingSwitchRef.current;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        completeSwitchNow(projectId, forwardPrompt);
      });
    });
  }

  // Trigger entrance animation when messages arrive after a context switch
  useEffect(() => {
    if (pendingEnterRef.current && messages.length > 0 && !transitioning) {
      pendingEnterRef.current = false;
      setMessagesHidden(false);
      setEntering(true);
      setTimeout(() => setEntering(false), 600);
    }
  }, [messages.length, transitioning]);

  // ── Message handler ──

  const handleMessage = useCallback((msg: ServerMessage) => {
    switch (msg.type) {
      case "text_delta": {
        if (modeRef.current === "concierge") {
          // Concierge: accumulate streaming chunks into one message
          const csid = streamingIdRef.current;
          if (!csid) {
            setIsBusy(false);
            const aid = crypto.randomUUID();
            streamingIdRef.current = aid;
            animatedMsgIds.current.add(aid);
            setMessages((prev) => [
              ...prev,
              { id: aid, role: "assistant", content: msg.content, streaming: true, timestamp: new Date() },
            ]);
          } else {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === csid ? { ...m, content: m.content + msg.content } : m,
              ),
            );
          }
        } else {
          // Project: streaming chunks
          const sid = streamingIdRef.current;
          if (!sid) {
            const newId = crypto.randomUUID();
            streamingIdRef.current = newId;
            animatedMsgIds.current.add(newId);
            setMessages((prev) => [
              ...prev,
              { id: newId, role: "assistant", content: msg.content, streaming: true, timestamp: new Date() },
            ]);
          } else {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === sid ? { ...m, content: m.content + msg.content } : m,
              ),
            );
          }
        }
        break;
      }
      case "text_done": {
        const sid = streamingIdRef.current;
        if (sid) {
          lastAssistantIdRef.current = sid;
          // Use the clean content from backend (state tags stripped) if provided
          const cleanContent = msg.content as string | undefined;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === sid
                ? { ...m, streaming: false, ...(cleanContent ? { content: cleanContent } : {}) }
                : m,
            ),
          );
          streamingIdRef.current = null;
        }
        // Track whether this response hit the budget limit
        const msgRecord = msg as Record<string, unknown>;
        lastBudgetStatusRef.current = msgRecord.budget_status === "hit_limit" ? "budget" : "complete";
        lastPushedBranchRef.current = msgRecord.pushed_branch as string | undefined;
        lastCanContinueRef.current = !!msgRecord.can_continue;
        // Diff data may arrive inline with text_done (preferred) or via separate work_diff
        if (msgRecord.diff_files) {
          setLastDiffFiles(msgRecord.diff_files as unknown[]);
        }
        // Iteration data for single-parameter tweaking
        if (msgRecord.iteration_data) {
          setLastIterationData(msgRecord.iteration_data);
        }
        continuingRef.current = false;
        setIsBusy(false);
        break;
      }
      case "debug_summary": {
        const aid = lastAssistantIdRef.current;
        if (aid) {
          setDebugSummaries((prev) => ({ ...prev, [aid]: msg as DebugSummary }));
        }
        break;
      }
      case "error": {
        streamingIdRef.current = null;
        setIsBusy(false);
        if (modeRef.current === "concierge") {
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: "assistant", content: "Something went wrong. Try again?", timestamp: new Date() },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: "error", content: msg.message, timestamp: new Date() },
          ]);
        }
        break;
      }
      case "activity": {
        // If mid-stream, finalize the current assistant bubble so the
        // next text_delta creates a new one — prevents all iterations
        // from mashing into a single paragraph.
        const actSid = streamingIdRef.current;
        if (actSid) {
          lastAssistantIdRef.current = actSid;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === actSid ? { ...m, streaming: false } : m,
            ),
          );
          streamingIdRef.current = null;
        }
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "activity", content: msg.description, timestamp: new Date() },
        ]);
        break;
      }
      case "widget": {
        // Handle iteration_applied as a state update, not a new message
        if (msg.widget_type === "iteration_applied" && msg.data) {
          const d = msg.data as Record<string, unknown>;
          const updatedIterData = d.iteration_data;
          // Update iteration state for all cards (latest card gets it)
          setIterationStates((prev) => {
            const updated = { ...prev };
            for (const key of Object.keys(updated)) {
              if (updated[key]?.applying) {
                updated[key] = { ...updated[key], applying: false, showPush: true, data: updatedIterData };
              }
            }
            return updated;
          });
          // Also update the lastIterationData for the live card
          if (updatedIterData) {
            setLastIterationData(updatedIterData);
          }
          // Update the card's summary text to replace old value
          if (d.param_name && d.new_value) {
            const unit = (d.unit as string) || "";
            const newVal = `${d.new_value}${unit}`;
            setMessages((prev) => {
              // Find the last assistant message and update its content
              const lastAssistant = [...prev].reverse().find((m) => m.role === "assistant" && !m.streaming);
              if (lastAssistant) {
                return prev.map((m) =>
                  m.id === lastAssistant.id
                    ? { ...m, content: m.content.replace(/\b\d+(\.\d+)?\s*(px|rem|em|%|vh|vw|pt|s|ms|deg)?/g, (match) => {
                        // Only replace if it looks like it could be the old value
                        return match;
                      })}
                    : m,
                );
              }
              return prev;
            });
          }
          setIsBusy(false);
          break;
        }
        // Clear iteration pushing state when push result arrives
        if (msg.widget_type === "git_push_result") {
          setIterationStates((prev) => {
            const updated = { ...prev };
            for (const key of Object.keys(updated)) {
              if (updated[key]?.pushing) {
                updated[key] = { ...updated[key], pushing: false };
              }
            }
            return updated;
          });
        }
        // Workflow run: update existing widget in-place by run_key so the
        // initial "dispatching" spinner becomes the terminal success/failure
        // state on the same row instead of stacking a second widget below.
        if (msg.widget_type === "workflow_run" && msg.data?.run_key) {
          const runKey = msg.data.run_key as string;
          setMessages((prev) => {
            const existingIdx = prev.findIndex(
              (m) =>
                m.role === "widget" &&
                m.widgetType === "workflow_run" &&
                (m.widgetData as any)?.run_key === runKey,
            );
            if (existingIdx >= 0) {
              return prev.map((m, i) =>
                i === existingIdx ? { ...m, widgetData: msg.data } : m,
              );
            }
            const wid = crypto.randomUUID();
            animatedMsgIds.current.add(wid);
            return [
              ...prev,
              {
                id: wid,
                role: "widget" as const,
                content: "",
                timestamp: new Date(),
                widgetType: msg.widget_type,
                widgetData: msg.data,
                widgetActions: msg.actions,
              },
            ];
          });
          break;
        }
        // Deploy progress: update existing widget in-place by deployment_id
        if (msg.widget_type === "deploy_progress" && msg.data?.deployment_id) {
          const depId = msg.data.deployment_id as string;
          setMessages((prev) => {
            const existingIdx = prev.findIndex(
              (m) =>
                m.role === "widget" &&
                (m.widgetType === "deploy_progress" || m.widgetType === "deploy_confirm") &&
                ((m.widgetData as any)?.deployment_id === depId ||
                 (m.widgetData as any)?.pending_key?.includes(depId)),
            );
            if (existingIdx >= 0) {
              // Update existing widget in-place
              return prev.map((m, i) =>
                i === existingIdx
                  ? { ...m, widgetType: "deploy_progress", widgetData: msg.data }
                  : m,
              );
            }
            // No existing widget — append as new
            const wid = crypto.randomUUID();
            animatedMsgIds.current.add(wid);
            return [
              ...prev,
              {
                id: wid,
                role: "widget" as const,
                content: "",
                timestamp: new Date(),
                widgetType: msg.widget_type,
                widgetData: msg.data,
                widgetActions: msg.actions,
              },
            ];
          });
          break;
        }
        // Use the backend-supplied message_id when present so this widget
        // dedupes against the same row delivered by api.projects.messages
        // (e.g. when the init effect's history fetch races the WS event).
        // Without this, a clarify widget can appear twice — or worse, get
        // overwritten when init's setMessages(history) runs after the
        // WS event but the row is in both sources.
        const backendMessageId = (msg as Record<string, unknown>).message_id as string | undefined;
        const wid = backendMessageId || crypto.randomUUID();
        animatedMsgIds.current.add(wid);
        setMessages((prev) => {
          const newWidget: ChatMessage = {
            id: wid,
            role: "widget",
            content: "",
            timestamp: new Date(),
            widgetType: msg.widget_type,
            widgetData: msg.data,
            widgetActions: msg.actions,
          };
          const existingIdx = prev.findIndex((m) => m.id === wid);
          if (existingIdx >= 0) {
            return prev.map((m, i) => (i === existingIdx ? newWidget : m));
          }
          return [...prev, newWidget];
        });
        setIsBusy(false);
        break;
      }
      case "project_renamed": {
        setProject((prev) =>
          prev ? { ...prev, name: msg.new_name } : prev,
        );
        break;
      }
      case "session_updated":
        break;
      case "session_rotated": {
        activeSessionRef.current = msg.new_session_id;
        break;
      }
      case "greeting":
        break;
      case "route_to_logs": {
        navigate("/logs");
        break;
      }
      case "route_to_project": {
        // Skip chat bubble exit animations when coming straight from hero
        const skip = heroOriginRef.current;
        heroOriginRef.current = false;
        switchContext(msg.project_id, msg.forward_prompt, skip);
        break;
      }
      case "project_deleted": {
        // Project was deleted — navigate home
        console.log("[ChatShell] project_deleted received, navigating home");
        switchContext(null, undefined, true);
        break;
      }
      case "context_usage": {
        setContextUsage({
          percent: msg.percent,
          shouldWarn: msg.percent >= 80,
        });
        break;
      }
      case "response_start": {
        setSuggestions([]);
        break;
      }
      case "work_start": {
        // If the last message is an assistant (streaming or finalized) with
        // content (e.g. gathering text flowing into scaffolding), tag it with
        // taskType and finalize it instead of creating a new empty message.
        // This keeps them in one WorkProgressCard.
        lastTaskTypeRef.current = (msg as Record<string, unknown>).task_type as string | undefined;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === "assistant" && last.content) {
            // Tag (and finalize if streaming) existing assistant
            streamingIdRef.current = null;
            return prev.map((m, i) =>
              i === prev.length - 1
                ? { ...m, streaming: false, taskType: lastTaskTypeRef.current }
                : m,
            );
          }
          // No recent assistant with content — seed with empty message
          const wsId = crypto.randomUUID();
          streamingIdRef.current = wsId;
          return [
            ...prev,
            { id: wsId, role: "assistant" as const, content: "", streaming: true, timestamp: new Date(), taskType: lastTaskTypeRef.current },
          ];
        });
        break;
      }
      case "work_diff": {
        // Diff data arrives after text_done — store in ref so the card picks it up
        setLastDiffFiles((msg as Record<string, unknown>).diff_files as unknown[] | undefined);
        break;
      }
      case "suggestions": {
        setSuggestions(msg.items);
        break;
      }
      case "workspace_reset": {
        // Carryover #6: the Cloudflare Worker had to recover the
        // workspace mid-call because the sandbox container was recycled
        // between sessions (or restoreBackup failed and fell through to
        // a re-clone). Uncommitted local work from the previous session
        // is gone. Surface it as a plain assistant message so it lands
        // in the transcript next to whatever the AI is about to say.
        const noticeId = crypto.randomUUID();
        animatedMsgIds.current.add(noticeId);
        const detail = msg.detail || "Your sandbox was reset. Any uncommitted work from the previous session is gone.";
        setMessages((prev) => [
          ...prev,
          {
            id: noticeId,
            role: "assistant" as const,
            content: detail,
            timestamp: new Date(),
          },
        ]);
        break;
      }
      case "github_status": {
        // GitHub disconnected/reconnected — refresh project to update repo info
        if (activeProjectId) {
          api.projects.get(activeProjectId).then(setProject).catch(() => {});
        }
        if (!msg.connected) {
          // GitHub was disconnected (app uninstalled, suspended, deleted, or user action).
          // For a deleted repo we skip the reconnect widget — the repo is
          // gone, so reconnecting the same installation wouldn't help.
          // The settings panel drives the user toward connecting a
          // different repo instead.
          const reasonCode = "reason_code" in msg ? msg.reason_code : undefined;
          const isDeleted = reasonCode === "deleted";
          const noticeId = crypto.randomUUID();
          animatedMsgIds.current.add(noticeId);
          const updates: ChatMessage[] = [
            {
              id: noticeId,
              role: "assistant" as const,
              content: msg.message || "GitHub was disconnected.",
              timestamp: new Date(),
            },
          ];
          if (!isDeleted) {
            const widgetId = crypto.randomUUID();
            animatedMsgIds.current.add(widgetId);
            updates.push({
              id: widgetId,
              role: "widget" as const,
              content: "",
              timestamp: new Date(),
              widgetType: "github_connect",
              widgetData: {},
              widgetActions: [{ id: "connect", label: "Reconnect GitHub", style: "primary" }],
            });
          }
          setMessages((prev) => [...prev, ...updates]);
        }
        break;
      }
      case "task_resume": {
        // Backend is replaying buffered events from a task that was running
        // while we were disconnected. Clean up stale state so replayed events
        // rebuild cleanly.
        taskResumeRef.current = true;

        // Switch to the session that has the active task
        if (msg.session_id && msg.session_id !== activeSessionRef.current) {
          activeSessionRef.current = msg.session_id;
        }

        // Clear any stale streaming state from the previous connection
        streamingIdRef.current = null;
        setMessages((prev) => prev.filter((m) => !m.streaming));

        if (msg.is_running) {
          setIsBusy(true);
        }

        // The replayed events follow immediately over WebSocket and will be
        // processed by the normal handlers above. Clear the resume flag after
        // enough time for all buffered events to arrive (they're sent in rapid
        // succession but each is a separate WebSocket message).
        setTimeout(() => { taskResumeRef.current = false; }, 5000);
        break;
      }
      case "onboarding_start": {
        setOnboarding(true);
        setIsBusy(true);
        break;
      }
      case "onboarding_end": {
        setOnboarding(false);
        setIsBusy(false);
        break;
      }
      case "turn_complete": {
        // Authoritative end-of-turn signal from the backend task
        // runner. Always fires, even if the handler crashed or the
        // websocket dropped mid-stream, so this is the single
        // reliable cue to clear the busy state. Idempotent — firing
        // it when we're not stuck is a no-op. When it arrives as a
        // replay on reconnect (msg.replay === true) it's catching up
        // a turn that completed while we were disconnected; we still
        // want to clear any lingering streaming/busy state so the UI
        // doesn't look wedged.
        if (streamingIdRef.current) {
          const streamId = streamingIdRef.current;
          setMessages((prev) =>
            prev.map((m) => (m.id === streamId ? { ...m, streaming: false } : m)),
          );
          streamingIdRef.current = null;
        }
        continuingRef.current = false;
        setIsBusy(false);
        setOnboarding(false);
        break;
      }
    }
  }, [navigate, switchContext, activeProjectId]);

  // ── Connection ──

  const { state: wsState, agentConnected, agentWaking, agentReason, sendChat, sendAction } = useChatConnection({
    activeProjectId,
    onMessage: handleMessage,
  });

  // Track connection failure after a timeout when waking
  const [agentWakeFailed, setAgentWakeFailed] = useState(false);
  useEffect(() => {
    if (agentWaking && !agentConnected) {
      setAgentWakeFailed(false);
      const timer = setTimeout(() => setAgentWakeFailed(true), 45_000);
      return () => clearTimeout(timer);
    }
    if (agentConnected) {
      setAgentWakeFailed(false);
    }
  }, [agentWaking, agentConnected]);

  // ── Auto-resume after agent-offline abort ──
  // If the agent went offline and the task was aborted (backend retries exhausted),
  // automatically send "continue" when the agent comes back so the user doesn't
  // have to manually re-trigger.
  const taskAbortedOfflineRef = useRef(false);
  const prevAgentConnected = useRef(agentConnected);
  useEffect(() => {
    const wasDisconnected = !prevAgentConnected.current;
    prevAgentConnected.current = agentConnected;

    // Agent just went offline while busy → mark as aborted
    // Exclude onboarding: isBusy is true during onboarding while the agent
    // hasn't connected yet, which would falsely trigger auto-resume once it does.
    if (!agentConnected && isBusy && !onboarding) {
      taskAbortedOfflineRef.current = true;
    }

    // Agent came back and we have a pending offline abort → auto-resume
    if (agentConnected && wasDisconnected && taskAbortedOfflineRef.current) {
      taskAbortedOfflineRef.current = false;
      const sid = activeSessionRef.current;
      if (sid && !isBusy) {
        // Brief delay to let the backend fully register the reconnection
        setTimeout(() => {
          sendChat("continue", sid);
          setIsBusy(true);
        }, 1000);
      }
    }
  }, [agentConnected, isBusy, sendChat]);

  // Auto-recover when WebSocket reconnects while busy.
  // If the connection dropped mid-response, text_done was lost and
  // the widget is stuck. Reload messages from the API to catch up.
  const prevWsState = useRef(wsState);
  useEffect(() => {
    const wasDisconnected = prevWsState.current === "disconnected";
    prevWsState.current = wsState;

    if (wsState === "connected" && wasDisconnected && isBusy && activeProjectId) {
      // Wait briefly for text_done or task_resume to arrive on the fresh connection
      const timer = setTimeout(async () => {
        // If task_resume already handled the recovery, skip API fetch
        if (taskResumeRef.current || !isBusy) return;
        // Still busy = text_done never came → messages were lost.
        // Don't check streamingIdRef here — it may be stale from a text_delta
        // that arrived before the disconnect. The API fetch is safe regardless:
        // it only loads a message if the DB has a newer completed response.
        try {
          const msgs = await api.projects.messages(activeProjectId, { limit: 20 });
          if (msgs.length > 0) {
            const latest = msgs[0]!;
            // If the latest DB message is newer than our last message,
            // reload it into the chat
            const lastLocal = messages[messages.length - 1];
            if (
              latest.role === "assistant" &&
              (!lastLocal || lastLocal.role !== "assistant" ||
                new Date(latest.created_at) > lastLocal.timestamp)
            ) {
              // Expand with work_steps if available (same logic as init)
              const log = latest.activity_log as Record<string, unknown> | null;
              const steps = log?.work_steps as Array<{ text?: string; activities: string[] }> | undefined;
              const ts = new Date(latest.created_at);

              if (steps && steps.length >= 2) {
                const wasBudgetHit = !!log?.hit_budget;
                const logPushedBranch = log?.pushed_branch as string | undefined;
                const logDiffFiles = log?.diff_files as unknown[] | undefined;
                const logTaskType = (log?.classifier as Record<string, unknown> | undefined)?.task_type as string | undefined;
                const expanded: ChatMessage[] = [];
                for (let si = 0; si < steps.length; si++) {
                  const step = steps[si]!;
                  expanded.push({ id: `${latest.id}-s${si}`, role: "assistant", content: step.text || "", timestamp: ts, ...(si === 0 && logTaskType ? { taskType: logTaskType } : {}) });
                  for (let ai = 0; ai < step.activities.length; ai++) {
                    expanded.push({ id: `${latest.id}-s${si}-a${ai}`, role: "activity", content: step.activities[ai]!, timestamp: ts });
                  }
                }
                const lastAst = [...expanded].reverse().find((m) => m.role === "assistant");
                if (lastAst) {
                  if (wasBudgetHit) lastAst.budgetHit = true;
                  if (wasBudgetHit && log?.can_continue) lastAst.canContinue = true;
                  if (logPushedBranch) lastAst.pushedBranch = logPushedBranch;
                  if (logDiffFiles) lastAst.diffFiles = logDiffFiles;
                }
                setMessages((prev) => [...prev.filter((m) => !m.streaming), ...expanded]);
              } else {
                setMessages((prev) => [
                  ...prev.filter((m) => !m.streaming),
                  { id: latest.id, role: "assistant", content: latest.content, timestamp: ts },
                ]);
              }
              setIsBusy(false);
              streamingIdRef.current = null;
            }
          }
        } catch {
          // Best-effort recovery
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [wsState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mobile resume: catch up on missed messages when page becomes visible ──
  // The WebSocket recovery above relies on wsState transitions, but on mobile
  // the transition may have already happened (and the timer expired) while the
  // page was suspended. This listener runs when the user actually returns.
  const isBusyRef = useRef(isBusy);
  isBusyRef.current = isBusy;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const activeProjectIdRef = useRef(activeProjectId);
  activeProjectIdRef.current = activeProjectId;

  useEffect(() => {
    const handleVisibilityResume = async () => {
      if (document.visibilityState !== "visible") return;
      if (!isBusyRef.current || !activeProjectIdRef.current) return;

      // Give the WebSocket a moment to deliver any queued text_done
      await new Promise((r) => setTimeout(r, 1500));

      // If text_done or task_resume arrived during the wait, nothing to do
      if (!isBusyRef.current || taskResumeRef.current) return;

      // Don't check streamingIdRef — it may be stale from a text_delta that
      // arrived before the page was backgrounded. The API fetch is safe: it only
      // loads a message if the DB has a newer completed response than local state.
      let recovered = false;
      try {
        const msgs = await api.projects.messages(activeProjectIdRef.current, { limit: 20 });
        if (msgs.length > 0) {
          const latest = msgs[0]!;
          const lastLocal = messagesRef.current[messagesRef.current.length - 1];
          if (
            latest.role === "assistant" &&
            (!lastLocal || lastLocal.role !== "assistant" ||
              new Date(latest.created_at) > lastLocal.timestamp)
          ) {
            const log = latest.activity_log as Record<string, unknown> | null;
            const steps = log?.work_steps as Array<{ text?: string; activities: string[] }> | undefined;
            const ts = new Date(latest.created_at);

            if (steps && steps.length >= 2) {
              const wasBudgetHit = !!log?.hit_budget;
              const logPushedBranch = log?.pushed_branch as string | undefined;
              const logTaskType2 = (log?.classifier as Record<string, unknown> | undefined)?.task_type as string | undefined;
              const expanded: ChatMessage[] = [];
              for (let si = 0; si < steps.length; si++) {
                const step = steps[si]!;
                expanded.push({ id: `${latest.id}-s${si}`, role: "assistant", content: step.text || "", timestamp: ts, ...(si === 0 && logTaskType2 ? { taskType: logTaskType2 } : {}) });
                for (let ai = 0; ai < step.activities.length; ai++) {
                  expanded.push({ id: `${latest.id}-s${si}-a${ai}`, role: "activity", content: step.activities[ai]!, timestamp: ts });
                }
              }
              const lastAst2 = [...expanded].reverse().find((m) => m.role === "assistant");
              if (lastAst2) {
                if (wasBudgetHit) lastAst2.budgetHit = true;
                if (wasBudgetHit && log?.can_continue) lastAst2.canContinue = true;
                if (logPushedBranch) lastAst2.pushedBranch = logPushedBranch;
              }
              setMessages((prev) => [...prev.filter((m) => !m.streaming), ...expanded]);
            } else {
              setMessages((prev) => [
                ...prev.filter((m) => !m.streaming),
                { id: latest.id, role: "assistant", content: latest.content, timestamp: ts },
              ]);
            }
            recovered = true;
          }
        }
      } catch {
        // Best-effort — a full refresh still works as fallback
      }
      // Only clear busy state if we found a completed response.
      // If the task is still running, the WebSocket reconnect will resume streaming.
      if (recovered) {
        setIsBusy(false);
        streamingIdRef.current = null;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityResume);
    return () => document.removeEventListener("visibilitychange", handleVisibilityResume);
  }, []); // Stable — reads current values from refs

  // ── Project initialization (project mode only) ──

  const refreshProject = useCallback(() => {
    if (activeProjectId) {
      api.projects.get(activeProjectId).then(setProject).catch(() => {});
    }
  }, [activeProjectId]);

  useEffect(() => {
    if (!activeProjectId) return;
    api.projects.get(activeProjectId).then(setProject).catch(console.error);

    // Restore branch from localStorage immediately, then fetch fresh from backend
    const savedBranch = localStorage.getItem(`branch_${activeProjectId}`);
    if (savedBranch) {
      setCurrentBranch(savedBranch);
    }

    api.projects.currentBranch(activeProjectId)
      .then((r) => {
        // Only update if backend returns a real branch (not "unknown")
        if (r.branch && r.branch !== "unknown") {
          setCurrentBranch(r.branch);
          localStorage.setItem(`branch_${activeProjectId}`, r.branch);
        }
      })
      .catch(() => {
        // Keep the localStorage value if backend fails
        if (!savedBranch) setCurrentBranch(null);
      });
  }, [activeProjectId]);

  const handleBranchSwitch = useCallback((branch: string, created: boolean) => {
    const prevBranch = currentBranch;
    setCurrentBranch(branch);
    if (activeProjectId) {
      localStorage.setItem(`branch_${activeProjectId}`, branch);
    }
    // Add a widget message to the chat
    const widgetMsg: ChatMessage = {
      id: `branch-${Date.now()}`,
      role: "widget",
      content: "",
      timestamp: new Date(),
      widgetType: "branch_switched",
      widgetData: {
        branch,
        previous_branch: prevBranch,
        created,
      },
    };
    setMessages((prev) => [...prev, widgetMsg]);
    // Trigger codebase sync
    if (activeSessionRef.current) {
      sendChat(
        created
          ? `[branch_created:${branch}]`
          : `[branch_switched:${branch}]`,
        activeSessionRef.current,
      );
    }
  }, [currentBranch, sendChat]);

  useEffect(() => {
    if (!activeProjectId) return;

    let cancelled = false;

    async function init() {
      setLoadingHistory(true);
      try {
        const session = await api.projects.activeSession(activeProjectId!);
        if (cancelled) return;
        activeSessionRef.current = session.id;

        const projectMessages = await api.projects.messages(activeProjectId!, { limit: 50 });
        if (cancelled) return;

        const activeSid = session.id;
        const history: ChatMessage[] = projectMessages
          .reverse()
          .filter((m) => {
            // Intent_confirm is a live confirmation prompt that expires
            // once the classifier moves on — no value replaying it.
            if (m.widget_json?.widget_type === "intent_confirm") return false;
            // Hide stale unanswered clarify widgets from older sessions —
            // they'd appear in history as clickable and trigger a stale
            // reinject. Current-session unanswered clarifies still load
            // so the user can pick up where they left off after refresh.
            if (m.widget_json?.widget_type === "clarify") {
              const data = m.widget_json?.data as Record<string, unknown> | undefined;
              if (!data?.answered && m.session_id !== activeSid) return false;
            }
            // Filter manual continuation signals so adjacent responses merge into one work card
            if (m.role === "user" && m.content === "[continue]") return false;
            // Filter reinjected clarified prompts — the clarify widget
            // already shows the Q&A summary in its answered state, so
            // rendering the raw "[clarified]..." user message again
            // would duplicate the information as an ugly chat bubble.
            if (m.role === "user" && m.content.startsWith("[clarified]")) return false;
            // Filter raw setup tags that leaked into saved messages (legacy data)
            if (m.role === "assistant" && m.content.includes("<setup_questions>")) return false;
            if (m.role === "assistant" && m.content.includes("<setup_ready>")) return false;
            // Filter empty assistant messages (e.g. tag-only setup responses)
            if (m.role === "assistant" && !m.content.trim()) return false;
            return true;
          })
          .flatMap((m) => {
            if (m.widget_json && m.widget_json.type === "widget") {
              return [{
                id: m.id,
                role: "widget" as const,
                content: m.content,
                timestamp: new Date(m.created_at),
                widgetType: m.widget_json.widget_type as string,
                widgetData: m.widget_json.data as Record<string, unknown>,
                widgetActions: m.widget_json.actions as Array<{ id: string; label: string; style?: string }> | undefined,
              }];
            }
            // Expand assistant messages with work_steps into interleaved
            // (assistant text → activity) pairs so groupMessages can form work_sequences
            const log = m.activity_log as Record<string, unknown> | null;
            const steps = log?.work_steps as Array<{ text?: string; activities: string[] }> | undefined;
            if (m.role === "assistant" && steps && steps.length >= 2) {
              const ts = new Date(m.created_at);
              const wasBudgetHit = !!log?.hit_budget;
              const logPushedBranch = log?.pushed_branch as string | undefined;
              const logDiffFiles = log?.diff_files as unknown[] | undefined;
              const logTaskType3 = (log?.classifier as Record<string, unknown> | undefined)?.task_type as string | undefined;
              const expanded: ChatMessage[] = [];
              for (let si = 0; si < steps.length; si++) {
                const step = steps[si]!;
                // Always emit an assistant message (even empty) so groupMessages
                // can pair it with the following activities into a work_sequence.
                // Without this, activity-only steps (e.g. "Syncing codebase...")
                // become standalone activity items on reload instead of being
                // part of the card.
                expanded.push({
                  id: `${m.id}-s${si}`,
                  role: "assistant",
                  content: step.text || "",
                  timestamp: ts,
                  ...(si === 0 && logTaskType3 ? { taskType: logTaskType3 } : {}),
                });
                for (let ai = 0; ai < step.activities.length; ai++) {
                  expanded.push({
                    id: `${m.id}-s${si}-a${ai}`,
                    role: "activity",
                    content: step.activities[ai]!,
                    timestamp: ts,
                  });
                }
              }
              // Mark last *assistant* message with metadata — activity messages
              // get batched by groupMessages and lose extra properties.
              const logIterationData = log?.iteration_data as unknown | undefined;
              const lastAssistant = [...expanded].reverse().find((m) => m.role === "assistant");
              if (lastAssistant) {
                if (wasBudgetHit) lastAssistant.budgetHit = true;
                if (wasBudgetHit && log?.can_continue) lastAssistant.canContinue = true;
                if (logPushedBranch) lastAssistant.pushedBranch = logPushedBranch;
                if (logDiffFiles) lastAssistant.diffFiles = logDiffFiles;
                if (logIterationData) lastAssistant.iterationData = logIterationData;
              }
              return expanded;
            }
            return [{
              id: m.id,
              role: m.role as "user" | "assistant",
              content: m.content,
              timestamp: new Date(m.created_at),
            }];
          });

        // For brand-new projects (empty history), the WS onboarding flow
        // races this init: the backend streams the greeting and the
        // clarify widget over WebSocket while api.projects.messages() is
        // still in flight. A naive setMessages(history) would erase
        // those just-arrived messages and leave the chat empty.
        //
        // For projects with existing history, the bug doesn't apply —
        // and naively merging could revive stale streaming chunks from
        // pre-refresh state, so we only protect prev when history is
        // empty (the precise case where the race exists). Backend now
        // persists [clarify] before streaming and tags the WS event with
        // the row's message_id, so it dedupes against history naturally
        // if both sources happen to carry it.
        setMessages((prev) => {
          if (history.length > 0 || prev.length === 0) return history;
          const historyIds = new Set(history.map((h) => h.id));
          const stragglers = prev.filter((m) => !historyIds.has(m.id));
          return stragglers.length === 0 ? history : [...history, ...stragglers];
        });
        setHasMore(projectMessages.length >= 50);
        setDebugSummaries({});
        resetScroll();
      } catch (err) {
        console.error("Failed to initialize chat:", err);
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [activeProjectId]);

  // Forward a prompt into the current context
  useEffect(() => {
    const prompt = forwardPromptRef.current;
    if (!prompt || wsState !== "connected") return;
    if (!isConcierge && !activeSessionRef.current) return;

    forwardPromptRef.current = null;

    const timer = setTimeout(() => {
      setIsBusy(true);
      resetScroll();
      const uid = crypto.randomUUID();
      animatedMsgIds.current.add(uid);
      setMessages((prev) => [
        ...prev,
        { id: uid, role: "user", content: prompt, timestamp: new Date() },
      ]);
      if (isConcierge) {
        sendChat(prompt);
      } else {
        sendChat(prompt, activeSessionRef.current!);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [wsState, sendChat, resetScroll, isConcierge]);

  // ── OAuth detection (concierge only, runs once) ──

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("github") === "connected") {
      if (window.opener) {
        localStorage.setItem("github-connected", Date.now().toString());
        window.close();
        return;
      }

      window.history.replaceState({}, "", "/");
      // Refresh project — auto-reconnect may have restored repo links
      if (activeProjectId) {
        api.projects.get(activeProjectId).then(setProject).catch(() => {});
      }
      api.github.status().then((status) => {
        if (status.connected) {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: "widget",
              content: "",
              timestamp: new Date(),
              widgetType: "github_status",
              widgetData: {
                installations: status.installations,
                just_connected: true,
              },
            },
          ]);
        }
      }).catch(() => {});
    }

  }, []);

  // Listen for GitHub OAuth popup completing (sets localStorage from popup tab)
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key === "github-connected") {
        // Refresh project data — auto-reconnect may have restored repo links
        if (activeProjectId) {
          api.projects.get(activeProjectId).then(setProject).catch(() => {});
        }
      }
    }
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [activeProjectId]);

  // iOS touch interception
  useEffect(() => {
    const inputArea = inputAreaRef.current;
    const scrollEl = scrollRef.current;
    const textareaEl = inputRef.current;
    if (!inputArea) return;

    let startY = 0;
    let touchTarget: HTMLElement | null = null;

    function onTouchStart(e: TouchEvent) {
      startY = e.touches[0]!.clientY;
      touchTarget = e.target as HTMLElement;
    }

    function onTouchMove(e: TouchEvent) {
      const dy = startY - e.touches[0]!.clientY;
      startY = e.touches[0]!.clientY;

      e.preventDefault();

      if (
        textareaEl &&
        touchTarget?.tagName === "TEXTAREA" &&
        textareaEl.scrollHeight > textareaEl.clientHeight
      ) {
        textareaEl.scrollTop += dy;
        return;
      }

      if (scrollEl) {
        scrollEl.scrollTop += dy;
      }
    }

    inputArea.addEventListener("touchstart", onTouchStart, { passive: true });
    inputArea.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      inputArea.removeEventListener("touchstart", onTouchStart);
      inputArea.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  // Relative time ticker
  useEffect(() => {
    if (isConcierge) return;
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, [isConcierge]);

  // ── Load older messages (project mode) ──

  const loadOlderMessages = useCallback(async () => {
    if (!activeProjectId || loadingOlder || !hasMore) return;

    const oldestMsg = messages.find((m) => m.role === "user" || m.role === "assistant");
    if (!oldestMsg) return;

    setLoadingOlder(true);
    try {
      const older = await api.projects.messages(activeProjectId, {
        limit: 30,
        before: oldestMsg.id,
      });

      if (older.length === 0) {
        setHasMore(false);
        return;
      }

      if (older.length < 30) {
        setHasMore(false);
      }

      const activeSid = activeSessionRef.current;
      const olderChat: ChatMessage[] = older
        .reverse()
        .filter((m) => {
          if (m.widget_json?.widget_type === "intent_confirm") return false;
          if (m.widget_json?.widget_type === "clarify") {
            const data = m.widget_json?.data as Record<string, unknown> | undefined;
            if (!data?.answered && m.session_id !== activeSid) return false;
          }
          return true;
        })
        .map((m) => {
          if (m.widget_json && m.widget_json.type === "widget") {
            return {
              id: m.id,
              role: "widget" as const,
              content: m.content,
              timestamp: new Date(m.created_at),
              widgetType: m.widget_json.widget_type as string,
              widgetData: m.widget_json.data as Record<string, unknown>,
              widgetActions: m.widget_json.actions as Array<{ id: string; label: string; style?: string }> | undefined,
            };
          }
          return {
            id: m.id,
            role: m.role as "user" | "assistant",
            content: m.content,
            timestamp: new Date(m.created_at),
          };
        });

      const el = scrollRef.current;
      const prevHeight = el?.scrollHeight ?? 0;
      const prevScroll = el?.scrollTop ?? 0;

      setMessages((prev) => [...olderChat, ...prev]);

      requestAnimationFrame(() => {
        if (el) {
          const newHeight = el.scrollHeight;
          el.scrollTop = prevScroll + (newHeight - prevHeight);
        }
      });
    } catch (err) {
      console.error("Failed to load older messages:", err);
    } finally {
      setLoadingOlder(false);
    }
  }, [activeProjectId, messages, loadingOlder, hasMore]);

  // ── Scroll handler ──

  function handleScroll() {
    onAutoScroll();

    if (!isConcierge) {
      const el = scrollRef.current;
      if (el && el.scrollTop < 100 && hasMore && !loadingOlder) {
        loadOlderMessages();
      }
    }
  }

  // ── Send handler ──

  function handleSend() {
    if (isBusy) return;

    const text = input.trim();
    if (!text) return;
    if (!isConcierge && !activeSessionRef.current) return;

    // Trigger hero → normal collapse animation
    if (heroPhase === "hero") {
      heroOriginRef.current = true;
      setHeroPhase("collapsing");
      // Clear greeting from animated set so it doesn't re-animate as a bubble
      setMessages((prev) => {
        for (const m of prev) {
          if (m.role === "assistant") animatedMsgIds.current.delete(m.id);
        }
        return prev;
      });
      heroTimerRef.current = setTimeout(() => {
        setHeroPhase("done");
        setInputRevealing(true);
        setTimeout(() => setInputRevealing(false), 500);
      }, 700);
    }

    setInput("");
    setSuggestions([]);
    setIsBusy(true);
    lastBudgetStatusRef.current = "complete"; // Reset for new message
    lastPushedBranchRef.current = undefined;
    lastCanContinueRef.current = false;
    lastTaskTypeRef.current = undefined;
    setLastDiffFiles(undefined);
    setLastIterationData(undefined);
    resetScroll();

    // Dismiss pending confirmation widgets — user chose to type instead.
    // Answered clarify widgets stay as chat history; only unanswered
    // (still-pending) clarify widgets get dismissed when the user types.
    setMessages((prev) => prev.filter((m) => {
      if (m.widgetType === "intent_confirm") return false;
      if (m.widgetType === "clarify") {
        const data = m.widgetData as Record<string, unknown> | undefined;
        if (!data?.answered) return false;
      }
      return true;
    }));

    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }

    const uid = crypto.randomUUID();
    animatedMsgIds.current.add(uid);
    setMessages((prev) => [
      ...prev,
      { id: uid, role: "user", content: text, timestamp: new Date() },
    ]);

    if (isConcierge) {
      sendChat(text);
    } else {
      sendChat(text, activeSessionRef.current!);
    }
  }

  function handleContinue() {
    if (isBusy || !activeSessionRef.current) return;

    // Clear budget hit on the last assistant message so the card
    // transitions from "Paused" → "Working" seamlessly.
    setMessages((prev) => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i]!.role === "assistant" && updated[i]!.budgetHit) {
          updated[i] = { ...updated[i]!, budgetHit: undefined, canContinue: undefined };
          break;
        }
      }
      // Insert a continuation separator activity so the card shows a divider
      updated.push({
        id: crypto.randomUUID(),
        role: "activity",
        content: "__continuation__",
        timestamp: new Date(),
      });
      return updated;
    });

    continuingRef.current = true;
    setIsBusy(true);
    lastBudgetStatusRef.current = "complete";
    lastCanContinueRef.current = false;
    lastTaskTypeRef.current = undefined;
    setLastDiffFiles(undefined);
    setLastIterationData(undefined);
    resetScroll();

    // Send the continuation signal — backend strips the brackets and
    // routes through the CONTINUATION fast path. No user bubble is added.
    sendChat("[continue]", activeSessionRef.current!);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // On mobile, Enter creates a new line — send via button only.
    // On desktop, Enter sends and Shift+Enter creates a new line.
    if (e.key === "Enter" && !e.shiftKey && getDeviceKind() === "desktop") {
      e.preventDefault();
      handleSend();
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.35) + "px";
  }

  function handleSuggestionClick(item: SuggestionItem) {
    setSuggestions([]);
    if (item.action === "switch_project") {
      const pid = item.payload.project_id as string;
      const pname = item.payload.project_name as string;
      if (pid && pname) {
        // If hero is showing, animate collapse then switch directly (skip chat bubbles)
        if (isConcierge && heroPhase === "hero") {
          setHeroPhase("collapsing");
          heroTimerRef.current = setTimeout(() => {
            setHeroPhase("done");
            setInputRevealing(true);
            setTimeout(() => setInputRevealing(false), 500);
            switchContext(pid, undefined, true);
          }, 700);
        } else {
          switchContext(pid);
        }
        return;
      }
    }
    // Mark hero origin so route_to_project can skip chat bubble animations
    if (isConcierge && heroPhase === "hero") {
      heroOriginRef.current = true;
    }
    sendAction(item.action, item.payload);
  }

  const renderItems = useMemo(() => groupMessages(messages), [messages]);
  const hasInput = input.trim().length > 0;

  // Derive repo display name and connection status label (project mode)
  const repoName = project?.connected_repo?.repo_full_name?.split("/").pop() ?? project?.name;
  const connectionLabel = agentConnected
    ? "Connected"
    : agentWakeFailed
      ? "Unable to connect"
      : agentWaking
        ? `Connecting to ${repoName ?? "repo"}...`
        : project?.connected_repo
          ? "Agent offline"
          : "No repo connected";

  // Max width depends on mode
  const maxW = isConcierge ? "max-w-xl" : "max-w-2xl";

  // ── Render message content ──

  function renderMessage(msg: ChatMessage, consecutive = false) {
    const isNew = animatedMsgIds.current.has(msg.id);
    const isUserMsg = msg.role === "user";
    const animClass = transitioning
      ? isUserMsg ? "animate-msg-exit-right" : "animate-msg-exit-left"
      : entering
        ? isUserMsg ? "animate-msg-enter-right" : "animate-msg-enter-left"
        : isNew
          ? isUserMsg ? "animate-msg-send" : "animate-msg-receive"
          : "";

    const gap = consecutive ? "mt-1.5" : "mt-3 first:mt-0";

    return (
      <div
        key={msg.id}
        onAnimationEnd={transitioning ? handleAnimationEnd : undefined}
        className={`${gap} ${animClass} ${
          msg.role === "user" ? "flex items-start justify-end" : ""
        }`}
      >
        {msg.role === "user" ? (
          /* ── User bubble ── */
          <>
            <div className="group/msg max-w-[88%] sm:max-w-[75%] md:max-w-[65%] shrink-0">
              <div className="rounded-3xl rounded-br-lg px-4 py-3 text-sm leading-relaxed text-daydra-text break-words overflow-hidden" style={{ background: bubbleGradient(msg.id, USER_GRADIENTS) }}>
                {msg.content.includes("\n") ? (
                  <div className="[&>p]:mb-2 [&>p:last-child]:mb-0">
                    {msg.content.split("\n\n").map((block, i) => (
                      <p key={i}>
                        {block.split("\n").map((line, j, arr) => (
                          <span key={j}>
                            {line.startsWith("**") && line.endsWith("**")
                              ? <strong className="text-daydra-text">{line.slice(2, -2)}</strong>
                              : line}
                            {j < arr.length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                ) : msg.content}
              </div>
              {!isConcierge && (
                <div className="flex items-center justify-end gap-1.5 mt-1.5 mr-2 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-normal">
                  <CopyMessageButton content={msg.content} />
                  <span className="text-2xs text-daydra-faint">
                    {relativeTime(msg.timestamp)}
                  </span>
                </div>
              )}
            </div>
          </>
        ) : msg.role === "error" ? (
          /* ── Error ── */
          <div className="rounded-2xl bg-daydra-error/[0.06] px-4 py-3 text-sm text-daydra-error/80 break-words overflow-hidden">
            <Linkify text={msg.content} />
          </div>
        ) : msg.role === "widget" && msg.widgetType ? (
          /* ── Widget ── */
          <WidgetRenderer
            widgetType={msg.widgetType}
            data={msg.widgetData ?? {}}
            actions={msg.widgetActions}
            onAction={(actionId, context) => {
              // Dismissable widgets (e.g. intent_confirm) remove themselves and show thinking
              if (context?.__dismiss) {
                setMessages(prev => prev.filter(m => m.id !== msg.id));
                setIsBusy(true);
              } else if (context?.__busy) {
                // Widget stays visible (e.g. clarify flipping to its
                // answered summary in place) but the pipeline is about
                // to run, so show the thinking indicator.
                setIsBusy(true);
              }

              // Clarify widgets transition to their answered summary in
              // place when submitted. Stamp the answered flag onto the
              // local message copy so (a) the summary renders from props
              // without relying on widget-local state and (b) the stale
              // -widget filter in sendChat preserves this as history
              // instead of treating it as a pending prompt.
              if (actionId === "clarify_response" && msg.widgetType === "clarify") {
                const payload = (context?.__payload ?? {}) as Record<string, unknown>;
                const rawAnswers = (payload.answers ?? {}) as Record<string, string>;
                const cleanAnswers: Record<string, string> = {};
                for (const [k, v] of Object.entries(rawAnswers)) {
                  if (typeof v === "string" && v.trim()) {
                    cleanAnswers[String(k)] = v;
                  }
                }
                setMessages(prev => prev.map(m => {
                  if (m.id !== msg.id) return m;
                  return {
                    ...m,
                    widgetData: {
                      ...(m.widgetData ?? {}),
                      answered: true,
                      answers: cleanAnswers,
                    },
                  };
                }));
              }

              if (context?.__action) {
                sendAction(context.__action as string, context.__payload as Record<string, unknown> ?? {});
              } else if (isConcierge) {
                sendChat(actionId);
              } else if (activeSessionRef.current) {
                sendChat(actionId, activeSessionRef.current);
              }
            }}
            onWidgetInputFocus={setWidgetInputFocused}
          />
        ) : isConcierge ? (
          /* ── Concierge assistant bubble: plain text ── */
          <div className="max-w-full sm:max-w-[90%] rounded-3xl rounded-bl-lg px-4 py-3 text-sm leading-relaxed text-daydra-text/[0.9]" style={{ background: bubbleGradient(msg.id, ASSISTANT_GRADIENTS) }}>
            <Linkify text={msg.content} />
          </div>
        ) : (
          /* ── Project assistant bubble: Markdown + debug ── */
          <div className="group/msg max-w-full sm:max-w-[90%] md:max-w-[85%]">
            <div className="rounded-3xl rounded-bl-lg px-4 py-3 text-daydra-text/[0.9] break-words overflow-hidden" style={{ background: bubbleGradient(msg.id, ASSISTANT_GRADIENTS) }}>
              <Markdown content={msg.content} />
              {msg.streaming && (
                <span className="inline-block w-[2px] h-[1.1em] bg-daydra-accent/60 rounded-full ml-0.5 align-middle animate-blink" />
              )}
            </div>
            {!msg.streaming && msg.content.length > 0 && (
              <div className="flex items-center gap-1.5 mt-1.5 ml-2 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-normal">
                <span className="text-2xs text-daydra-faint">
                  {relativeTime(msg.timestamp)}
                </span>
                <CopyMessageButton content={msg.content} />
                {debugSummaries[msg.id] && (
                  <DebugBar summary={debugSummaries[msg.id]!} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── JSX ──

  return (
    <div
      className="flex h-full bg-daydra-bg"
      style={{
        background: 'linear-gradient(to bottom, hsl(var(--surface-hue), var(--surface-sat), 8%) 0%, var(--daydra-bg) 40%, hsl(var(--surface-hue), var(--surface-sat), 12%) 100%)',
      }}
    >
      {/* ── Desktop sidebar (inline, pushes content) ── */}
      <HeaderMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        currentProject={isConcierge ? null : project}
        onGoHome={() => switchContext(null, undefined, true)}
        onProjectRefresh={refreshProject}
        onSwitchProject={(id) => switchContext(id, undefined, true)}
      />

      {/* ── Main content column ── */}
      <div className="relative flex flex-col flex-1 min-w-0 h-full">
      {/* ── Message area (header is sticky inside the scroller) ── */}
      <div
        ref={scrollRef}
        data-scroll-container
        onScroll={handleScroll}
        className={`relative flex-1 overflow-x-hidden overscroll-contain ${
          isConcierge && heroPhase !== "done" ? "overflow-y-hidden" : "overflow-y-auto"
        }`}
      >
        {/* ── Header ── */}
        <header className="flex items-center gap-3 px-5 sm:px-6 py-3 pt-4 sm:pt-3 sticky top-0 z-40 bg-[hsla(var(--surface-hue),var(--surface-sat),9%,0.7)] backdrop-blur-md shadow-[0_1px_12px_rgba(0,0,0,0.3)]">
          {/* Logo — opens sidebar */}
          <button
            onClick={() => setMenuOpen(true)}
            className={cn(
              "shrink-0 text-white/50 hover:text-daydra-accent transition-all duration-300",
              menuOpen && "md:opacity-0 md:pointer-events-none",
            )}
            title="Menu"
          >
            <DaydraLogo size={28} />
          </button>

          {/* Branch label (center) — project mode only */}
          {!isConcierge && project ? (
            <BranchSwitcher
              projectId={activeProjectId!}
              projectName={project.name}
              repoFullName={project.connected_repo?.repo_full_name ?? ""}
              currentBranch={currentBranch}
              onBranchSwitch={handleBranchSwitch}
            />
          ) : (
            <div className="flex-1 min-w-0 flex justify-center">
              <span className="text-sm font-medium text-daydra-text">Daydra</span>
            </div>
          )}

          {/* Project settings — right side hamburger */}
          {!isConcierge ? (
            <button
              onClick={() => setSettingsOpen(true)}
              className={cn(
                "relative shrink-0 h-7 w-7 flex items-center justify-center rounded-lg text-daydra-dim hover:text-daydra-text hover:bg-white/[0.06] transition-all duration-fast",
                settingsOpen && "md:opacity-0 md:pointer-events-none",
              )}
              title={`Project settings — ${connectionLabel}`}
              aria-label="Project settings"
            >
              <Menu size={16} />
              {/* Connection status dot — preserves at-a-glance agent state */}
              <span
                className={cn(
                  "absolute top-1 right-1 h-1.5 w-1.5 rounded-full transition-colors duration-slow",
                  agentConnected
                    ? "bg-daydra-success animate-pulse-soft"
                    : agentWakeFailed
                      ? "bg-daydra-error"
                      : agentWaking
                        ? "bg-daydra-faint animate-pulse-soft"
                        : "bg-daydra-faint",
                )}
              />
            </button>
          ) : (
            <div className="w-7 shrink-0" />
          )}
        </header>

        {/* Context usage — project mode only */}
        {!isConcierge && contextUsage && contextUsage.percent >= 40 && (
          <div className="h-px mx-4 sm:mx-5 rounded-full overflow-hidden bg-daydra-raised">
            <div
              className={`h-full rounded-full transition-all duration-slower ease-out ${
                contextUsage.shouldWarn ? "bg-daydra-warning/70" : "bg-daydra-accent/30"
              }`}
              style={{ width: `${contextUsage.percent}%` }}
            />
          </div>
        )}

        {/* Agent provisioning / waking banner — project mode only.
            Shows the human-readable reason from the agent_status WS
            broadcast (e.g. "Setting up your agent now.") so the user
            knows why their first request might lag. Auto-disappears
            when the agent broadcasts connected=true. */}
        {!isConcierge && agentWaking && !agentConnected && (
          <div className="mx-4 sm:mx-5 mt-2 px-3 py-2 rounded-lg bg-daydra-accent/10 border border-daydra-accent/20 flex items-center gap-2">
            <Loader2 size={12} className="animate-spin text-daydra-accent shrink-0" />
            <span className="text-xs text-daydra-text/90">
              {agentReason ?? "Bringing your agent online..."}
            </span>
          </div>
        )}

        {/* ── Hero greeting — centered splash on concierge first load ── */}
        {isConcierge && heroPhase !== "done" && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center px-5 sm:px-6"
            style={{
              top: "48px",
              transition: heroPhase === "collapsing"
                ? "opacity 500ms ease-out, transform 700ms cubic-bezier(0.22, 1, 0.36, 1)"
                : "none",
              opacity: heroPhase === "collapsing" ? 0 : 1,
              transform: heroPhase === "collapsing"
                ? "translateY(-30vh) scale(0.6)"
                : "translateY(0) scale(1)",
            }}
          >
            <div className="flex flex-col items-center gap-6 w-full max-w-xl">
              {messages.length === 1 && messages[0]?.role === "assistant" && (
                <p
                  className={`${heroFontSize(messages[0]!.content)} text-center leading-snug sm:leading-relaxed max-w-lg font-extralight tracking-wide`}
                  style={{
                    animation: "fadeIn 2.4s cubic-bezier(0.7, 0, 0.3, 1)",
                    color: "rgba(255,255,255,0.5)",
                    maskImage: "linear-gradient(to top, rgba(0,0,0,0.3) 0%, black 60%)",
                    WebkitMaskImage: "linear-gradient(to top, rgba(0,0,0,0.3) 0%, black 60%)",
                  }}
                >
                  {messages[0]!.content}
                </p>
              )}
              {/* Inline input — lives right below the greeting */}
              <div
                className="w-full bg-daydra-surface/95 backdrop-blur-xl rounded-2xl px-3 shadow-daydra-glow ring-1 ring-white/[0.04]"
                style={{ animation: "fadeIn 2s ease-out 1.8s both" }}
              >
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={wsState === "connected" ? "Message..." : "Connecting..."}
                  disabled={wsState !== "connected"}
                  rows={2}
                  className="w-full resize-none bg-transparent pt-3 pb-1 text-sm text-daydra-text placeholder:text-daydra-muted/50 focus:outline-none disabled:opacity-40 touch-auto"
                />
                {/* Bottom row — buttons only */}
                <div className="flex items-center justify-between pb-2 pt-0.5">
                  {suggestions.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 px-0.5">
                      {suggestions.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => handleSuggestionClick(s)}
                          className="text-xs font-medium px-3 py-1.5 rounded-xl border border-daydra-border bg-daydra-raised text-daydra-text hover:brightness-125 transition-all duration-fast"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div />
                  )}
                  {isBusy ? (
                    <button
                      className="shrink-0 p-1.5 rounded-full text-daydra-muted hover:text-daydra-error transition-colors"
                      title="Stop"
                    >
                      <Square size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={handleSend}
                      disabled={!hasInput || wsState !== "connected"}
                      className={`shrink-0 p-1.5 rounded-full transition-all duration-normal ${
                        hasInput
                          ? "text-daydra-bg hover:brightness-110 scale-100"
                          : "text-daydra-muted/40 scale-95"
                      } disabled:opacity-30 disabled:scale-95`}
                      style={hasInput ? { background: "var(--brand-gradient)" } : undefined}
                      title="Send"
                    >
                      <ArrowUp size={16} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`${maxW} mx-auto min-w-0 px-4 sm:px-5 pt-4 ${widgetInputFocused ? "pb-4" : "pb-40"} ${messagesHidden ? "invisible" : ""}`}
          style={isConcierge && heroPhase !== "done" ? {
            visibility: "hidden" as const,
          } : undefined}
        >

          {/* Loading older (project mode) */}
          {!isConcierge && loadingOlder && (
            <div className="flex justify-center py-4">
              <Loader2 size={14} className="text-daydra-muted animate-spin" />
            </div>
          )}

          {/* Loading history (project mode) */}
          {!isConcierge && loadingHistory && messages.length === 0 && (
            <div className="flex h-full items-center justify-center min-h-[200px]">
              <Loader2 size={16} className="text-daydra-muted animate-spin" />
            </div>
          )}

          {/* Connecting state (project mode only — concierge loading handled by hero) */}
          {!isConcierge && !loadingHistory && messages.length === 0 && wsState !== "connected" && (
            <div className="flex h-full items-center justify-center min-h-[200px]">
              <p className="text-sm text-daydra-muted animate-fade-in">Connecting...</p>
            </div>
          )}

          {/* ── Messages ── */}
          {isConcierge ? (
            // Concierge: flat message list
            messages.map((msg, i) => {
              const prev = messages[i - 1];
              const sameSide = prev ? (prev.role === "user") === (msg.role === "user") : false;
              return renderMessage(msg, sameSide);
            })
          ) : (
            // Project: grouped with dates, activities, and work sequences
            (() => {
              let prevSide: "user" | "other" | null = null;
              return renderItems.map((item, idx) => {
                if (item.kind === "date") {
                  prevSide = null;
                  return <DateSeparator key={item.id} date={item.date} />;
                }
                if (item.kind === "activities") {
                  const isLastItem = idx === renderItems.length - 1;
                  prevSide = null;
                  return (
                    <div key={item.ids[0]} className="mt-3">
                      <ActivityGroup
                        activities={item.texts}
                        active={isBusy && isLastItem}
                      />
                    </div>
                  );
                }
                if (item.kind === "work_sequence") {
                  const isLastItem = idx === renderItems.length - 1;
                  const isActive = isBusy && isLastItem;
                  // Budget status: from live ref for current response, from persisted activity_log for history
                  const status = isActive ? undefined
                    : item.budgetHit ? "budget" as const
                    : isLastItem ? lastBudgetStatusRef.current
                    : undefined;
                  // Can continue: from persisted activity_log or live ref
                  const canContinue = item.canContinue || (isLastItem ? lastCanContinueRef.current : false);
                  // Pushed branch: from persisted activity_log or live ref
                  const pushedBranch = item.pushedBranch || (isLastItem ? lastPushedBranchRef.current : undefined);
                  const taskType = item.taskType || (isLastItem ? lastTaskTypeRef.current : undefined);
                  // Iteration data: from persisted message or live ref
                  const cardIterState = iterationStates[item.id];
                  const rawIterData = cardIterState?.data || item.iterationData || (isLastItem ? lastIterationData : undefined);
                  const iterData = rawIterData as IterationData | undefined;
                  prevSide = "other";
                  return (
                    <div key={item.id} className="mt-5 first:mt-0 max-w-full sm:max-w-[90%] md:max-w-[85%] animate-msg-receive">
                      <WorkProgressCard
                        steps={item.steps}
                        active={isActive}
                        status={status}
                        canContinue={canContinue}
                        onContinue={handleContinue}
                        pushedBranch={pushedBranch}
                        diffFiles={item.diffFiles || (isLastItem ? lastDiffFiles : undefined)}
                        taskType={taskType}
                        iterationData={iterData}
                        iterationApplying={cardIterState?.applying}
                        iterationPushing={cardIterState?.pushing}
                        iterationShowPush={cardIterState?.showPush}
                        onIterationApply={(newValue, _updatedData) => {
                          setIterationStates((prev) => ({
                            ...prev,
                            [item.id]: { ...prev[item.id], applying: true },
                          }));
                          sendAction("iteration_apply", {
                            iteration_data: rawIterData,
                            new_value: newValue,
                          });
                        }}
                        onIterationPush={() => {
                          setIterationStates((prev) => ({
                            ...prev,
                            [item.id]: { ...prev[item.id], pushing: true },
                          }));
                          sendAction("iteration_push", {});
                        }}
                      />
                    </div>
                  );
                }
                const side: "user" | "other" = item.msg.role === "user" ? "user" : "other";
                const consecutive = prevSide === side;
                prevSide = side;
                return renderMessage(item.msg, consecutive);
              });
            })()
          )}

          {/* ── Typing indicator ── */}
          {isConcierge ? (
            isBusy && (
              <div className="mt-1.5 flex items-center gap-1.5 px-1 py-2 animate-fade-in">
                <span className="typing-dot" />
                <span className="typing-dot [animation-delay:150ms]" />
                <span className="typing-dot [animation-delay:300ms]" />
              </div>
            )
          ) : (
            isBusy && !streamingIdRef.current && (
              <div className="mt-1.5"><TypingIndicator /></div>
            )
          )}
        </div>
      </div>

      {/* ── Input area: floating pill ── */}
      {/* (fixed position, so lives inside the content column but visually floats) */}
      <div
        ref={inputAreaRef}
        className="absolute left-0 right-0 z-50 px-5 sm:px-6 pointer-events-none"
        style={{
          transition: "bottom 75ms ease, opacity 100ms ease",
          bottom: isKeyboardOpen ? "8px" : "24px",
          ...(widgetInputFocused
            ? { opacity: 0, pointerEvents: "none" as const, visibility: "hidden" as const }
            : {}),
          ...(isConcierge && heroPhase !== "done"
            ? { visibility: "hidden" as const, pointerEvents: "none" as const }
            : {}),
          ...(inputRevealing
            ? { animation: "inputReveal 500ms cubic-bezier(0.22, 1, 0.36, 1) both" }
            : {}),
        }}
      >
        <div className={`${maxW} mx-auto pointer-events-auto`}>
          {/* Scroll-to-bottom — project mode only */}
          {!isConcierge && (
            <div className="relative">
              <ScrollToBottom visible={isScrolledUp} onClick={scrollToBottom} />
            </div>
          )}
          <div className="bg-daydra-surface/95 backdrop-blur-xl rounded-2xl px-3 shadow-daydra-glow transition-shadow duration-normal focus-within:shadow-[0_0_24px_rgba(134,87,241,0.18)] ring-1 ring-white/[0.04]">
            {/* Textarea */}
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                onboarding
                  ? "Getting to know this project..."
                  : isConcierge
                    ? wsState === "connected" ? "Message..." : "Connecting..."
                    : wsState === "connected"
                      ? agentConnected
                        ? "Message..."
                        : agentWakeFailed
                          ? "Unable to connect"
                          : agentWaking
                            ? `Connecting to ${repoName ?? "repo"}...`
                            : project?.connected_repo
                              ? "Agent offline"
                              : "No repo connected"
                      : "Connecting..."
              }
              disabled={wsState !== "connected" || onboarding}
              rows={2}
              className="w-full resize-none bg-transparent pt-3 pb-1 text-sm text-daydra-text placeholder:text-daydra-muted/50 focus:outline-none disabled:opacity-40 touch-auto"
            />
            {/* Bottom row — buttons only */}
            <div className="flex items-center justify-between pb-2 pt-0.5">
              {suggestions.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 px-0.5">
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleSuggestionClick(s)}
                      className="text-xs font-medium px-3 py-1.5 rounded-xl border border-daydra-border bg-daydra-raised text-daydra-text hover:brightness-125 transition-all duration-fast"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div />
              )}
              {isBusy ? (
                <button
                  className="shrink-0 p-1.5 rounded-full text-daydra-muted hover:text-daydra-error transition-colors"
                  title="Stop"
                >
                  <Square size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!hasInput || wsState !== "connected"}
                  className={`shrink-0 p-1.5 rounded-full transition-all duration-normal ${
                    hasInput
                      ? "text-daydra-bg hover:brightness-110 scale-100"
                      : "text-daydra-muted/40 scale-95"
                  } disabled:opacity-30 disabled:scale-95`}
                  style={hasInput ? { background: "var(--brand-gradient)" } : undefined}
                  title="Send"
                >
                  <ArrowUp size={16} strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>{/* end content column */}

      {/* ── Project settings (right-side sidebar / mobile overlay) ── */}
      {!isConcierge && project && (
        <ProjectSettingsPanel
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          project={project}
          onProjectDeleted={() => {
            setSettingsOpen(false);
            switchContext(null, undefined, true);
          }}
          onProjectRefresh={refreshProject}
        />
      )}
    </div>
  );
}
